import React from 'react';
import { Phone, Mic, MicOff, Video, VideoOff, Sparkles } from 'lucide-react';

/**
 * PhoneCallModal - Modal for active phone calls with controls
 */
const PhoneCallModal = ({
  isOpen,
  call,
  isCalling,
  callDuration,
  isMuted,
  isVideoOn,
  onClose,
  onToggleMute,
  onToggleVideo,
  onEndCall,
  onAIAssist,
  className = ''
}) => {
  if (!isOpen || !call) return null;

  // Format duration as MM:SS
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className={`bg-gray-900 border border-gray-400/20 rounded-2xl w-[400px] p-8 flex flex-col items-center gap-6 ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Contact Avatar */}
        <div className="relative">
          {call.avatar ? (
            <img
              src={call.avatar}
              alt={call.name}
              className="w-32 h-32 rounded-full object-cover"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-gray-700 flex items-center justify-center">
              <Phone size={48} className="text-gray-400" />
            </div>
          )}
          {isCalling && (
            <div className="absolute inset-0 rounded-full border-4 border-green-500 animate-ping" />
          )}
        </div>

        {/* Contact Info */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-2">{call.name}</h3>
          <p className="text-gray-400 mb-1">{call.phone}</p>
          <p className="text-green-400 font-medium">
            {isCalling ? 'Calling...' : formatDuration(callDuration)}
          </p>
        </div>

        {/* Call Controls */}
        <div className="flex gap-4 mt-4">
          {/* Mute Button */}
          {onToggleMute && (
            <button
              onClick={onToggleMute}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                isMuted 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
            </button>
          )}

          {/* End Call Button */}
          <button
            onClick={onEndCall}
            className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
            title="End Call"
          >
            <Phone size={24} className="rotate-[135deg]" />
          </button>

          {/* Video Button */}
          {onToggleVideo && (
            <button
              onClick={onToggleVideo}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                isVideoOn 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
              title={isVideoOn ? 'Turn off video' : 'Turn on video'}
            >
              {isVideoOn ? <Video size={24} /> : <VideoOff size={24} />}
            </button>
          )}

          {/* AI Assist Button */}
          {onAIAssist && (
            <button
              onClick={onAIAssist}
              className="w-14 h-14 rounded-full bg-purple-500 hover:bg-purple-600 text-white flex items-center justify-center transition-colors"
              title="AI Assistant"
            >
              <Sparkles size={24} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneCallModal;
